// ONLY REPLACE the handleSubmit function in your existing LoanApplication.jsx
// AND add this import at the top:
//   import { useAuth } from '../contexts/AuthContext';
//   import { submitApplication } from '../utils/api';
// AND remove:
//   import { saveApplication } from '../utils/storage';
//
// Then replace handleSubmit with this:

/*
  const { token } = useAuth();   // add this line inside the component

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      try {
        await submitApplication(formData, token);  // token can be null (guest)
        alert('✅ Application submitted successfully! Our expert will call you soon.');
        navigate('/');
      } catch (error) {
        console.error('Error submitting application:', error);
        alert('⚠️ There was an error submitting your application. Please try again.');
      }
    }
  };
*/

// Full updated LoanApplication.jsx is below:

import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import BasicInfo from '../components/BasicInfo';
import AgeVerification from '../components/AgeVerification';
import GenderSelection from '../components/GenderSelection';
import CustomerProfile from '../components/CustomerProfile';
import { useAuth } from '../contexts/AuthContext';
import { submitApplication } from '../utils/api';

const LoanApplication = () => {
  const location = useLocation();
  const { token } = useAuth();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    loanType: location.state?.loanType || '',
    loanAmount: '',
    monthlySalary: '',
    name: '',
    phone: '',
    email: '',
    address: '',
    dob: '',
    age: null,
    gender: '',
    customerType: '',
    netMonthlySalary: '',
    monthlyEMI: '',
    businessType: '',
    annualTurnover: '',
    netProfit: '',
    otherIncome: '',
    existingEMI: '',
    applicationDate: new Date().toISOString()
  });
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const validate = () => {
    const newErrors = {};
    if (step === 1) {
      if (!formData.loanType) newErrors.loanType = 'Loan type is required';
      if (!formData.loanAmount || formData.loanAmount <= 0) newErrors.loanAmount = 'Valid loan amount is required';
      if (formData.customerType === 'Business Owner') {
        if (!formData.netProfit || formData.netProfit <= 0) newErrors.netProfit = 'Valid net profit is required';
      } else if (formData.customerType === 'Salaried') {
        if (!formData.monthlySalary || formData.monthlySalary <= 0) newErrors.monthlySalary = 'Valid monthly salary is required';
      }
      if (!formData.customerType) newErrors.customerType = 'Customer type is required';
      if (!formData.name) newErrors.name = 'Name is required';
      if (!formData.phone || !/^\d{10}$/.test(formData.phone)) newErrors.phone = 'Valid phone number is required';
      if (!formData.email || !/^\S+@\S+\.\S+$/.test(formData.email)) newErrors.email = 'Valid email is required';
      if (!formData.address) newErrors.address = 'Address is required';
    } else if (step === 2) {
      if (!formData.dob) {
        newErrors.dob = 'Date of birth is required';
      } else if (formData.age === null || formData.age < 21 || formData.age > 55) {
        newErrors.dob = 'You must be between 21-55 years old to be eligible for a loan';
      }
    } else if (step === 3) {
      if (!formData.gender) newErrors.gender = 'Gender selection is required';
    } else if (step === 4) {
      if (formData.customerType === 'Business Owner') {
        if (!formData.businessType) newErrors.businessType = 'Business type is required';
        if (formData.businessType && (!formData.annualTurnover || formData.annualTurnover <= 0))
          newErrors.annualTurnover = 'Valid annual turnover is required';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => { if (validate()) setStep(step + 1); };
  const prevStep = () => setStep(step - 1);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'dob') {
      const dob = new Date(value);
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age--;
      setFormData(prev => ({ ...prev, dob: value, age }));
      return;
    }
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      try {
        // Pass token so the application is linked to the logged-in user (can be null for guests)
        await submitApplication(formData, token);
        alert('✅ Application submitted successfully! Our expert will call you soon.');
        navigate(token ? '/my-applications' : '/');
      } catch (error) {
        console.error('Error submitting application:', error);
        alert('⚠️ There was an error submitting your application. Please try again.');
      }
    }
  };

  const steps = [
    <BasicInfo formData={formData} handleChange={handleChange} nextStep={nextStep} errors={errors} />,
    <AgeVerification formData={formData} handleChange={handleChange} nextStep={nextStep} prevStep={prevStep} errors={errors} />,
    <GenderSelection formData={formData} handleChange={handleChange} nextStep={nextStep} prevStep={prevStep} errors={errors} />,
    <CustomerProfile formData={formData} handleChange={handleChange} handleSubmit={handleSubmit} prevStep={prevStep} errors={errors} />
  ];

  return (
    <div className="loan-application">
      <div className="progress-bar">
        {[1, 2, 3, 4].map((stepNumber) => (
          <div
            key={stepNumber}
            className={`step ${stepNumber < step ? 'completed' : ''} ${stepNumber === step ? 'active' : ''}`}
          >
            {stepNumber}
          </div>
        ))}
      </div>
      {steps[step - 1]}
    </div>
  );
};

export default LoanApplication;
